/** Pure first-party event-ticketing rules shared by public and admin domains. */

export const EVENT_TICKET_HOLD_MS = 15 * 60 * 1000;
export const EVENT_TICKET_MIN_QUANTITY = 1;
export const EVENT_TICKET_MAX_QUANTITY = 10;
// Checkout capacity reads use `.take(capacity)`, so this keeps that query
// bounded while leaving non-ticket RSVP/event capacity policy unchanged.
export const MAX_FIRST_PARTY_EVENT_CAPACITY = 1000;

export type EventAdmissionMode =
  | "free_rsvp"
  | "external_ticket"
  | "first_party_ticket"
  | "none"
  | "giveaway";

type LegacyEventAdmissionInput = {
  admissionMode?: EventAdmissionMode | null;
  ticketLink?: string | null;
  status: string;
};

/**
 * Keep old events usable without a migration. A configured mode is always the
 * authority; only records with no explicit mode take the legacy path.
 */
export function resolveEventAdmissionMode(
  event: LegacyEventAdmissionInput,
): EventAdmissionMode {
  if (event.admissionMode) return event.admissionMode;
  if (event.ticketLink?.trim()) return "external_ticket";
  return event.status === "published" ? "free_rsvp" : "none";
}

export function isFirstPartyTicketSalesPaused(event: {
  admissionMode?: EventAdmissionMode | null;
  ticketSalesPaused?: boolean | null;
}): boolean {
  return (
    event.admissionMode === "first_party_ticket" &&
    event.ticketSalesPaused === true
  );
}

function assertSafeWholeCents(value: number, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${label} must be a non-negative whole cents value`);
  }
}

export function assertEventTicketQuantity(quantity: number): number {
  if (!Number.isInteger(quantity)) {
    throw new Error("Ticket quantity must be a whole number");
  }
  if (
    quantity < EVENT_TICKET_MIN_QUANTITY ||
    quantity > EVENT_TICKET_MAX_QUANTITY
  ) {
    throw new Error("Ticket quantity must be between 1 and 10");
  }
  return quantity;
}

export function calculateEventTicketTotals(input: {
  unitPriceCents: number;
  quantity: number;
  taxable: boolean;
  taxRate?: number;
}): { subtotalCents: number; taxCents: number; totalCents: number } {
  assertSafeWholeCents(input.unitPriceCents, "Ticket price");
  const quantity = assertEventTicketQuantity(input.quantity);
  const subtotalCents = input.unitPriceCents * quantity;
  if (!Number.isSafeInteger(subtotalCents)) {
    throw new Error("Ticket subtotal exceeds safe whole cents");
  }

  if (!input.taxable) {
    return { subtotalCents, taxCents: 0, totalCents: subtotalCents };
  }
  if (
    typeof input.taxRate !== "number" ||
    !Number.isFinite(input.taxRate) ||
    input.taxRate < 0 ||
    input.taxRate > 1
  ) {
    throw new Error("A taxable ticket requires a tax rate between 0 and 1");
  }
  const { numerator, denominator } = taxRateFraction(input.taxRate);
  const taxNumerator = BigInt(subtotalCents) * numerator;
  const taxQuotient = taxNumerator / denominator;
  const taxRemainder = taxNumerator % denominator;
  // The numerator is non-negative, so quotient/remainder rounding is exactly
  // half-up without carrying a binary floating-point approximation into money.
  const roundedTax =
    taxQuotient +
    (taxRemainder * BigInt(2) >= denominator ? BigInt(1) : BigInt(0));
  if (roundedTax > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error("Ticket tax exceeds safe whole cents");
  }
  const taxCents = Number(roundedTax);
  const totalCents = subtotalCents + taxCents;
  if (!Number.isSafeInteger(taxCents) || !Number.isSafeInteger(totalCents)) {
    throw new Error("Ticket total exceeds safe whole cents");
  }
  return { subtotalCents, taxCents, totalCents };
}

function taxRateFraction(rate: number): { numerator: bigint; denominator: bigint } {
  const serialized = rate.toString().toLowerCase();
  const match = /^(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/.exec(serialized);
  if (!match) throw new Error("Ticket tax rate must be a decimal number");
  const [, whole, fractional = "", exponentText] = match;
  const exponent = exponentText ? Number(exponentText) : 0;
  if (!Number.isSafeInteger(exponent)) {
    throw new Error("Ticket tax rate exponent is invalid");
  }
  const scale = fractional.length - exponent;
  if (scale < 0 || scale > 9) {
    throw new Error("Ticket tax rate must have no more than 9 decimal places");
  }
  const digits = `${whole}${fractional}`.replace(/^0+(?=\d)/, "") || "0";
  return {
    numerator: BigInt(digits),
    denominator: BigInt(10) ** BigInt(scale),
  };
}

export type EventTicketBuyer = {
  firstName: string;
  lastName: string;
  email: string;
  normalizedEmail: string;
  phone: string;
  normalizedPhone: string;
};

function requiredName(value: string, label: string): string {
  const normalized = value.trim().replace(/\s+/g, " ");
  if (!normalized || normalized.length > 100) {
    throw new Error(`Invalid ticket buyer ${label}`);
  }
  return normalized;
}

function normalizeTicketPhone(phone: string): string {
  const trimmed = phone.trim();
  if (!trimmed) {
    return "";
  }
  if (!/^[+()\s.\-\d]+$/.test(trimmed)) {
    throw new Error("Invalid ticket buyer phone");
  }
  const digits = trimmed.replace(/\D/g, "");
  const normalized =
    digits.length === 10
      ? `+1${digits}`
      : digits.length === 11 && digits.startsWith("1")
        ? `+${digits}`
        : trimmed.startsWith("+")
          ? `+${digits}`
          : "";
  if (!/^\+[1-9]\d{7,14}$/.test(normalized)) {
    throw new Error("Invalid ticket buyer phone");
  }
  return normalized;
}

export function normalizeEventTicketBuyer(input: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}): EventTicketBuyer {
  const firstName = requiredName(input.firstName, "first name");
  const lastName = requiredName(input.lastName, "last name");
  const email = input.email.trim();
  const normalizedEmail = email.toLowerCase();
  if (
    !normalizedEmail ||
    normalizedEmail.length > 254 ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
  ) {
    throw new Error("Invalid ticket buyer email");
  }
  const phone = input.phone.trim();
  return {
    firstName,
    lastName,
    email,
    normalizedEmail,
    phone,
    normalizedPhone: normalizeTicketPhone(input.phone),
  };
}

export function ticketHoldExpiresAt(now: number): number {
  if (!Number.isSafeInteger(now)) throw new Error("Hold time must be a timestamp");
  return now + EVENT_TICKET_HOLD_MS;
}

export function isTicketHoldActive(expiresAt: number | undefined, now: number): boolean {
  return typeof expiresAt === "number" && Number.isFinite(expiresAt) && expiresAt > now;
}

type EventTicketPaymentStatus =
  | "pending"
  | "paid"
  | "failed"
  | "refund_pending"
  | "refunded"
  | "disputed"
  | "cancelled";
type EventTicketPassStatus =
  | "pending"
  | "issued"
  | "checked_in"
  | "revoked";

const PAYMENT_TRANSITIONS: Record<EventTicketPaymentStatus, readonly EventTicketPaymentStatus[]> = {
  pending: ["paid", "failed", "cancelled"],
  paid: ["refund_pending", "disputed"],
  failed: [],
  refund_pending: ["paid", "refunded"],
  refunded: [],
  disputed: [],
  cancelled: [],
};

const PASS_TRANSITIONS: Record<EventTicketPassStatus, readonly EventTicketPassStatus[]> = {
  pending: ["issued", "revoked"],
  issued: ["checked_in", "revoked"],
  checked_in: [],
  revoked: [],
};

export function canTransitionEventTicketState(
  kind: "payment",
  from: EventTicketPaymentStatus,
  to: EventTicketPaymentStatus,
): boolean;
export function canTransitionEventTicketState(
  kind: "pass",
  from: EventTicketPassStatus,
  to: EventTicketPassStatus,
): boolean;
export function canTransitionEventTicketState(
  kind: "payment" | "pass",
  from: string,
  to: string,
): boolean {
  const transitions =
    kind === "payment"
      ? PAYMENT_TRANSITIONS[from as EventTicketPaymentStatus]
      : PASS_TRANSITIONS[from as EventTicketPassStatus];
  return transitions?.includes(to as never) ?? false;
}

function randomBytes(length: number): Uint8Array {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi?.getRandomValues) {
    throw new Error("Event ticket credentials require Web Crypto");
  }
  return cryptoApi.getRandomValues(new Uint8Array(length));
}

function hex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

const MANUAL_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

export function normalizeEventTicketManualCode(value: string): string {
  const compact = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
  const body = compact.startsWith("EVT") ? compact.slice(3) : compact;
  if (!/^[A-Z0-9]{8}$/.test(body)) {
    throw new Error("Invalid event ticket manual code");
  }
  return `EVT-${body.slice(0, 4)}-${body.slice(4)}`;
}

export function generateEventTicketCredentials(): {
  qrToken: string;
  manualCode: string;
} {
  const qrToken = `evtqr_${hex(randomBytes(32))}`;
  const manualBody = Array.from(randomBytes(8), (byte) =>
    MANUAL_ALPHABET[byte & 31],
  ).join("");
  return {
    qrToken,
    manualCode: normalizeEventTicketManualCode(manualBody),
  };
}

/** SHA-256 hex for indexed credential lookups; plaintext is never persisted. */
export async function hashEventTicketLookup(value: string): Promise<string> {
  if (!value) throw new Error("Cannot hash an empty event ticket credential");
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi?.subtle) throw new Error("Event ticket hashes require Web Crypto");
  const digest = await cryptoApi.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return hex(new Uint8Array(digest));
}
